import { useTheme } from '@emotion/react';
import { Comments, PreviewOpen, ThumbsDown, ThumbsUp } from '@icon-park/react';
import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { LEARNING_RESOURCE_TYPE } from '@didaskill/domain/learning-resources/constants';
import type {
  LearningResource,
  LearningResourceViewFull,
} from '@didaskill/domain/learning-resources/types';
import dayjs from '@didaskill/services/dayjs';
import { Avatar, IconBadge, Tag, Tooltip, Typography } from '@didaskill/ui/design-system';
import { globalMessages } from '@didaskill/ui/intl';

import { LearningResourcePicture } from '../picture/LearningResourcePicture';
import { LearningResourceDeleteButton } from './actions/delete/LearningResourceDeleteButton';
import { LearningResourceEditButton } from './actions/edit/LearningResourceEditButton';
import {
  LEARNING_RESOURCE_ICON_MAPPING,
  LEARNING_RESOURCE_ICON_VARIANT_MAPPING,
} from './constants';
import messages from './LearningResourceCard.messages';
import * as styles from './LearningResourceCard.styles';

export type LearningResourceCardProps = {
  learningResource: LearningResource;
  canUpdateLearningResource?: boolean;
  onClickUpdateLearningResource?: (learningResourceId: string) => void;
  canDeleteLearningResource?: boolean;
  onDeleteLearningResource?: (learningResourceId: string) => Promise<void>;
  fromReference?: boolean;
  onOpenEngagement?: (learningResourceId: string) => void;
};

type LearningResourceWithEngagementCounts = LearningResourceViewFull & {
  likesCount: number;
  dislikesCount: number;
  reportsCount: number;
};

/**
 * Returns whether the resource carries engagement totals from the backoffice list.
 * @param learningResource Resource rendered on the card.
 * @returns True when likes, dislikes and report counts are numbers.
 */
function hasEngagementCounts(
  learningResource: LearningResource,
): learningResource is LearningResourceWithEngagementCounts {
  const resourceWithCounts = learningResource as LearningResourceViewFull;

  return (
    typeof resourceWithCounts.likesCount === 'number' &&
    typeof resourceWithCounts.dislikesCount === 'number' &&
    typeof resourceWithCounts.reportsCount === 'number'
  );
}

/**
 * Renders a learning resource card with its views and, when provided, engagement counts.
 * @param learningResource Resource displayed on the card.
 * @param canUpdateLearningResource Whether the edit action is available.
 * @param onClickUpdateLearningResource Opens the resource edition form.
 * @param canDeleteLearningResource Whether the delete action is available.
 * @param onDeleteLearningResource Deletes the resource.
 * @param fromReference Whether the card is shown from a lesson reference.
 * @param onOpenEngagement Opens the engagement drawer for this resource.
 * @returns Learning resource card.
 */
export const LearningResourceCardBase = ({
  learningResource,
  canUpdateLearningResource = false,
  onClickUpdateLearningResource,
  canDeleteLearningResource = false,
  onDeleteLearningResource,
  fromReference = false,
  onOpenEngagement,
}: LearningResourceCardProps): JSX.Element => {
  const intl = useIntl();
  const theme = useTheme();

  const { picture, presenter, title, publishedAt, type, viewsCount } = learningResource;

  const withNewTag = useMemo(
    () => dayjs(publishedAt).isSameOrAfter(dayjs().subtract(1, 'month')),
    [publishedAt],
  );
  const titleElementRef = useRef<HTMLDivElement>(null);
  const [isTitleCropped, setIsTitleCropped] = useState(false);

  // The clamped box keeps a fixed height, so a title change does not resize it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: remeasure overflow when the title text changes
  useLayoutEffect(() => {
    const element = titleElementRef.current;

    if (!element) {
      return;
    }

    const measureTitleCrop = (): void => {
      setIsTitleCropped(element.scrollHeight > element.clientHeight);
    };

    measureTitleCrop();

    const observer = new ResizeObserver(measureTitleCrop);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [title]);

  const defaultPresenter = {
    id: 'default',
    email: 'default',
    fullName: intl.formatMessage(messages.DEFAULT_PRESENTER_FULLNAME),
  };

  const hasReferences = 'references' in learningResource;
  const nbLessonsAsReferences =
    (learningResource as LearningResourceViewFull).references?.lessons?.length || 0;
  const nbCoursesAsReferences =
    (learningResource as LearningResourceViewFull).references?.courses?.length || 0;

  return (
    <div css={styles.container}>
      <div css={styles.cardContainer}>
        <div css={styles.content}>
          <div css={styles.header}>
            <div css={styles.avatar}>
              <Avatar
                size={Avatar.SIZES.SMALL}
                user={presenter || defaultPresenter}
                useDefaultAvatar={!presenter}
              />
              <span css={styles.presenterName}>
                <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR} element="span">
                  {presenter?.fullName || defaultPresenter.fullName}
                </Typography>
              </span>
            </div>

            {withNewTag && (
              <Tag size={Tag.SIZES.SMALL} label={intl.formatMessage(globalMessages.NEW)} />
            )}
          </div>

          <LearningResourcePicture picture={picture}>
            {type === LEARNING_RESOURCE_TYPE.REPLAY && (
              <Tag
                css={styles.replayTag}
                variant={Tag.VARIANTS.NEUTRAL}
                size={Tag.SIZES.SMALL}
                label={intl.formatMessage(messages.REPLAY_TAG)}
              />
            )}
            <IconBadge
              size={IconBadge.SIZES.MEDIUM}
              variant={LEARNING_RESOURCE_ICON_VARIANT_MAPPING[type]}
              icon={LEARNING_RESOURCE_ICON_MAPPING[type]}
              css={styles.learningResourceTag}
            />
          </LearningResourcePicture>

          {hasReferences ? (
            <div css={styles.references}>
              {nbCoursesAsReferences > 0 ? (
                <Tag
                  label={intl.formatMessage(messages.REFERENCED_COURSES, {
                    count: nbCoursesAsReferences,
                  })}
                  size={Tag.SIZES.SMALL}
                  variant={Tag.VARIANTS.PRIMARY}
                  light
                />
              ) : null}
              {nbLessonsAsReferences > 0 ? (
                <Tag
                  label={intl.formatMessage(messages.REFERENCED_LESSONS, {
                    count: nbLessonsAsReferences,
                  })}
                  size={Tag.SIZES.SMALL}
                  variant={Tag.VARIANTS.SECONDARY}
                  light
                />
              ) : null}
            </div>
          ) : null}

          <Tooltip content={title} disabled={!isTitleCropped}>
            <div ref={titleElementRef} css={styles.title}>
              <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD} element="span">
                {title}
              </Typography>
            </div>
          </Tooltip>
        </div>

        <div css={styles.views}>
          <div css={styles.stats}>
            <div css={styles.statsGroup}>
              <span
                css={styles.data}
                role="img"
                aria-label={intl.formatMessage(globalMessages.viewCount, {
                  count: viewsCount || 0,
                })}
              >
                <PreviewOpen
                  fill={[theme.colors.gray[500], theme.colors.gray[0]]}
                  strokeWidth={4}
                />
                <Typography variant={Typography.VARIANTS.BODY_SMALL_BOLD} element="span">
                  {viewsCount || 0}
                </Typography>
              </span>

              {hasEngagementCounts(learningResource) ? (
                <>
                  <span
                    css={styles.data}
                    role="img"
                    aria-label={intl.formatMessage(messages.LIKES_COUNT, {
                      count: learningResource.likesCount,
                    })}
                  >
                    <ThumbsUp
                      fill={[theme.colors.success[500], theme.colors.gray[0]]}
                      strokeWidth={4}
                    />
                    <Typography variant={Typography.VARIANTS.BODY_SMALL_BOLD} element="span">
                      {learningResource.likesCount}
                    </Typography>
                  </span>
                  <span
                    css={styles.data}
                    role="img"
                    aria-label={intl.formatMessage(messages.DISLIKES_COUNT, {
                      count: learningResource.dislikesCount,
                    })}
                  >
                    <ThumbsDown
                      fill={[theme.colors.danger[500], theme.colors.gray[0]]}
                      strokeWidth={4}
                    />
                    <Typography variant={Typography.VARIANTS.BODY_SMALL_BOLD} element="span">
                      {learningResource.dislikesCount}
                    </Typography>
                  </span>
                </>
              ) : null}
            </div>

            {hasEngagementCounts(learningResource) && onOpenEngagement ? (
              <button
                type="button"
                css={[styles.data, styles.reportsButton]}
                aria-label={intl.formatMessage(messages.OPEN_ENGAGEMENT, {
                  count: learningResource.reportsCount,
                })}
                onClick={() => onOpenEngagement(learningResource.id)}
              >
                <Comments
                  fill={[theme.colors.primary[500], theme.colors.gray[0]]}
                  strokeWidth={4}
                />
                <Typography variant={Typography.VARIANTS.BODY_SMALL_BOLD} element="span">
                  {learningResource.reportsCount}
                </Typography>
              </button>
            ) : null}

            {hasEngagementCounts(learningResource) && !onOpenEngagement ? (
              <span
                css={styles.data}
                role="img"
                aria-label={intl.formatMessage(messages.OPEN_ENGAGEMENT, {
                  count: learningResource.reportsCount,
                })}
              >
                <Comments
                  fill={[theme.colors.primary[500], theme.colors.gray[0]]}
                  strokeWidth={4}
                />
                <Typography variant={Typography.VARIANTS.BODY_SMALL_BOLD} element="span">
                  {learningResource.reportsCount}
                </Typography>
              </span>
            ) : null}
          </div>

          <div css={styles.footer}>
            <div css={styles.publicationDate}>
              <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
                {dayjs(publishedAt).format('LL')}
              </Typography>
            </div>

            <div css={styles.actions}>
              {canUpdateLearningResource && onClickUpdateLearningResource && (
                <LearningResourceEditButton
                  learningResource={learningResource}
                  onUpdateLearningResource={onClickUpdateLearningResource}
                />
              )}

              {canDeleteLearningResource && onDeleteLearningResource && (
                <LearningResourceDeleteButton
                  learningResource={learningResource}
                  onDeleteLearningResource={onDeleteLearningResource}
                  fromReference={fromReference}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

LearningResourceCardBase.displayName = 'LearningResourceCard';

export const LearningResourceCard = memo(LearningResourceCardBase);
